import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import SEO from '../components/SEO';
import PageHeader from '../components/PageHeader';
import { MaskedLinesOnView, Reveal } from '../components/Reveal';
import { POLICY } from '../data/site';

export default function Policies() {
  return (
    <>
      <SEO
        title="No-Show & Cancellation Policy | Thula Doula Wellness"
        description="Our appointment attendance and cancellation policy: 24-hour notice required, late cancellation tracking, no-show handling, billing compliance, and waitlist information."
      />
      <PageHeader
        testId="policies-page-header"
        eyebrow="Policies"
        lines={['Attendance &', 'cancellation policy.']}
        sub="Because appointment availability is limited and many patients are waiting for care, scheduled appointments must be attended or rescheduled with appropriate notice."
        cta={false}
      />

      <section className="bg-brand-cream pb-24 md:pb-32" data-testid="policies-section">
        <div className="mx-auto max-w-7xl px-5 md:px-10">
          <Reveal>
            <div className="max-w-3xl space-y-5 border-l-2 border-brand-sage pl-6 md:pl-10">
              {POLICY.intro.map((p, i) => (
                <p key={i} className="text-base leading-relaxed text-brand-moss md:text-lg">
                  {i === 1 ? <strong className="font-semibold text-brand-ink">{p}</strong> : p}
                </p>
              ))}
            </div>
          </Reveal>

          <div className="mt-16 border-t border-brand-ink/10 md:mt-24">
            {POLICY.sections.map((section, i) => (
              <Reveal key={section.title} delay={0.05}>
                <article
                  data-testid={`policy-section-${i}`}
                  className="grid grid-cols-1 gap-6 border-b border-brand-ink/10 py-10 md:grid-cols-12 md:gap-10 md:py-14"
                >
                  <div className="md:col-span-4">
                    <span className="font-display text-lg italic text-brand-teal/60">
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <h2 className="mt-2 font-display text-3xl font-light leading-tight tracking-tight text-brand-ink md:text-4xl">
                      {section.title}
                    </h2>
                  </div>
                  <div className="md:col-span-8">
                    {section.paras.map((p, j) => (
                      <p key={j} className="max-w-2xl text-sm leading-relaxed text-brand-moss md:text-base">
                        {p}
                      </p>
                    ))}
                    {section.list && (
                      <ul className="mt-5 max-w-2xl space-y-2.5">
                        {section.list.map((item) => (
                          <li
                            key={item}
                            className="flex items-start gap-3 text-sm font-medium text-brand-ink md:text-base"
                          >
                            <span className="mt-2 size-1.5 shrink-0 rounded-full bg-brand-teal" aria-hidden="true" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    )}
                    {section.note && (
                      <p className="mt-5 max-w-2xl rounded-2xl bg-brand-mist/40 p-5 text-sm leading-relaxed text-brand-ink">
                        {section.note}
                      </p>
                    )}
                  </div>
                </article>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.1}>
            <div
              data-testid="policy-acknowledgment"
              className="mt-16 rounded-3xl bg-brand-ink p-8 text-brand-cream md:p-14"
            >
              <MaskedLinesOnView
                as="h2"
                lines={['Patient acknowledgment.']}
                className="font-display text-3xl font-light tracking-tight md:text-4xl"
              />
              <p className="mt-5 max-w-2xl text-sm leading-relaxed text-brand-cream/80 md:text-base">
                {POLICY.acknowledgment}
              </p>
              <p className="mt-6 max-w-2xl text-sm leading-relaxed text-brand-cream/60">
                Questions about this policy? Call us at (832) 350-8516 or{' '}
                <Link
                  to="/contact"
                  data-testid="policy-contact-link"
                  className="font-semibold text-brand-sage underline decoration-brand-sage/50 underline-offset-4 transition-colors duration-300 hover:text-brand-cream"
                >
                  send a message
                </Link>
                .
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="mt-16 text-center" data-testid="policies-cta">
              <MaskedLinesOnView
                as="h2"
                lines={[<span key="q" className="italic">Need an appointment?</span>]}
                className="font-display text-4xl font-light italic tracking-tight text-brand-ink md:text-5xl"
              />
              <p className="mx-auto mt-4 max-w-md text-sm leading-relaxed text-brand-moss md:text-base">
            With appropriate notice, rescheduling is always simple through your Jane confirmation
            email.
              </p>
              <div className="mt-8 flex justify-center">
                <a
                  data-testid="policies-book-btn"
                  href="https://thuladoulamassage.janeapp.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group inline-flex items-center gap-2.5 rounded-full bg-brand-teal px-8 py-4 text-sm font-semibold text-brand-cream transition-[background-color,transform] duration-300 hover:bg-brand-ink active:scale-[0.97]"
                >
                  Book a Massage
                  <ArrowUpRight className="size-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
